import { useEffect, useState } from 'react'
import { App, Card, Col, Row, Space, Statistic, Tag, Typography } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'

import {
  downloadMaintenanceReportCsv,
  maintenanceReportsApi,
  type ReportFilters,
} from '@/features/maintenance/api'
import DataTable from '@/shared/components/DataTable'
import PageHeader from '@/shared/components/PageHeader'
import { usePlantId } from '@/shared/hooks/usePlantId'
import ReportFilterBar from './components/ReportFilterBar'
import MaintenanceStatusBadge from './components/MaintenanceStatusBadge'
import { useMasterLookups } from './components/useMasterLookups'

const { Text } = Typography

interface Row {
  machine_id: string
  machine_code: string
  machine_name: string
  department_id: string | null
  machine_type: string | null
  breakdown_count: number
  work_order_count: number
  total_downtime_minutes: number
  total_downtime_hours: number
  avg_downtime_per_breakdown: number
  mttr_minutes: number
  mtbf_hours: number
  repeat_breakdown_count: number
  last_breakdown_at: string | null
  machine_status: string | null
  health_score: number
  maintenance_cost: number
  // Phase 3.1 — backend-calculated, never edited in UI
  production_kg: number | null
  cost_per_kg: number | null
  cost_per_kg_match_level: string
}

/**
 * Spec §4 — Machine-wise downtime report.
 * Drilldown: clicking a machine code links to the Machine Health page where
 * the per-machine history drawer surfaces every WO + PM + spare event.
 */
export default function MachineDowntimeReportPage() {
  const { message } = App.useApp()
  const { plantId } = usePlantId()
  const { deptName } = useMasterLookups()
  const [filters, setFilters] = useState<ReportFilters>({
    plant_id: plantId,
    from_date: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
    to_date: dayjs().format('YYYY-MM-DD'),
  })
  useEffect(() => {
    if (filters.plant_id !== plantId) setFilters((f) => ({ ...f, plant_id: plantId }))
  }, [plantId, filters.plant_id])

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['report-machine-downtime', filters],
    enabled: !!filters.plant_id,
    queryFn: () => maintenanceReportsApi.machineDowntime(filters) as unknown as Promise<Row[]>,
  })

  const rows = data ?? []
  const totalDowntime = rows.reduce((a, r) => a + (r.total_downtime_minutes ?? 0), 0)
  const totalBreakdowns = rows.reduce((a, r) => a + (r.breakdown_count ?? 0), 0)
  const totalCost = rows.reduce((a, r) => a + (r.maintenance_cost ?? 0), 0)

  const [exporting, setExporting] = useState(false)
  const exportCsv = async () => {
    setExporting(true)
    try {
      await downloadMaintenanceReportCsv(
        '/maintenance/reports/machine-downtime', filters,
        `machine-downtime-${dayjs().format('YYYYMMDD-HHmm')}.csv`,
      )
    } catch (e) {
      message.error((e as Error).message ?? 'Export failed')
    } finally {
      setExporting(false)
    }
  }

  const columns = [
    { title: 'Machine', dataIndex: 'machine_code', key: 'mc', width: 130,
      render: (v: string, r: Row) => (
        <Link to={`/maintenance/machine-health`}>{v ?? r.machine_id.slice(0, 8)}</Link>
      ),
    },
    { title: 'Name', dataIndex: 'machine_name', key: 'name' },
    { title: 'Department', key: 'dept', width: 140,
      render: (_: unknown, r: Row) => deptName(r.department_id),
    },
    { title: 'Type', dataIndex: 'machine_type', key: 'mt', width: 130 },
    { title: 'Breakdowns', dataIndex: 'breakdown_count', key: 'bk', width: 110 },
    { title: 'WOs', dataIndex: 'work_order_count', key: 'wo', width: 80 },
    { title: 'Downtime (min)', dataIndex: 'total_downtime_minutes', key: 'dt', width: 130 },
    { title: 'Downtime (h)',   dataIndex: 'total_downtime_hours',   key: 'dth', width: 110 },
    { title: 'Avg / BD (min)',  dataIndex: 'avg_downtime_per_breakdown', key: 'avg', width: 130 },
    { title: 'MTTR (min)', dataIndex: 'mttr_minutes', key: 'mttr', width: 110 },
    { title: 'MTBF (h)',   dataIndex: 'mtbf_hours',   key: 'mtbf', width: 110 },
    { title: 'Repeat',     dataIndex: 'repeat_breakdown_count', key: 'rep', width: 90 },
    { title: 'Last BD',    dataIndex: 'last_breakdown_at', key: 'lbd', width: 130,
      render: (v: string | null) => v ? dayjs(v).format('DD MMM HH:mm') : '—',
    },
    { title: 'Health', dataIndex: 'health_score', key: 'hs', width: 110,
      render: (v: number, r: Row) => (
        <Space>
          <Text strong>{v}</Text>
          <MaintenanceStatusBadge status={r.machine_status} />
        </Space>
      ),
    },
    {
      title: 'Status', dataIndex: 'machine_status', key: 'st', width: 130,
      render: (v: string | null) => v ? <Tag>{v}</Tag> : '—',
    },
    { title: 'Cost (₹)', dataIndex: 'maintenance_cost', key: 'mc2', width: 120,
      render: (v: number) => `₹ ${v.toFixed(2)}`,
    },
    // Phase 3.1 — production kg + cost/kg from the backend; the level tag
    // tells the user how specific the match was (machine+shift > dept > plant).
    { title: 'Production kg', dataIndex: 'production_kg', key: 'pk', width: 130,
      render: (v: number | null) => v == null ? <Text type="secondary">—</Text> : v.toFixed(2),
    },
    {
      title: 'Cost / kg (₹)', dataIndex: 'cost_per_kg', key: 'cpk', width: 160,
      render: (v: number | null, r: Row) => (
        <Space>
          {v == null
            ? <Text type="secondary">—</Text>
            : <Text strong>₹ {v.toFixed(4)}</Text>}
          {v != null && <Tag color="geekblue">{r.cost_per_kg_match_level}</Tag>}
        </Space>
      ),
    },
  ]

  return (
    <div>
      <PageHeader
        title="Machine-wise Downtime"
        subtitle="Identify weak machines. Sorted by downtime descending."
      />
      <ReportFilterBar
        filters={filters} onChange={setFilters} onRefresh={() => refetch()}
        onExport={exportCsv} exporting={exporting}
        show={{ dateRange: true, department: true, machineType: true }}
      />
      <Row gutter={12} style={{ marginBottom: 12 }}>
        <Col xs={12} md={6}><Card><Statistic title="Machines" value={rows.length} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Total breakdowns" value={totalBreakdowns} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Total downtime (min)" value={totalDowntime} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Maintenance cost" value={totalCost} prefix="₹" precision={2} /></Card></Col>
      </Row>
      <DataTable rowKey="machine_id" columns={columns} dataSource={rows} loading={isLoading} />
    </div>
  )
}
